export const API_ENDPOINTS = {
  ASSETS: {
    list: () => "/assets",
    detail: (id: string) => `/assets/${id}`,
    create: () => "/assets",
    update: (id: string) => `/assets/${id}`,
    activate: (id: string) => `/assets/${id}/activate`,
    delete: (id: string) => `/assets/${id}`,
    export: () => "/assets/export",
    fieldValues: (id: string) => `/assets/${id}/field-values`,
    documents: {
      list: (assetId: string) => `/assets/${assetId}/documents`,
      create: (assetId: string) => `/assets/${assetId}/documents`,
      link: (assetId: string) => `/assets/${assetId}/documents/link`,
      delete: (assetId: string, docId: string) => `/assets/${assetId}/documents/${docId}`,
    },
    copyRelations: () => "/assets/copy-relations",
  },
  ASSET_MODELS: {
    list: () => "/asset-models",
    detail: (id: string) => `/asset-models/${id}`,
    create: () => "/asset-models",
    update: (id: string) => `/asset-models/${id}`,
    archive: (id: string) => `/asset-models/${id}/archive`,
    unarchive: (id: string) => `/asset-models/${id}/unarchive`,
    duplicate: (id: string) => `/asset-models/${id}/duplicate`,
    compatibleParts: {
      delete: (modelId: string, partId: string) =>
        `/asset-models/${modelId}/compatible-parts/${partId}`,
    },
    customFields: {
      list: (modelId: string) => `/asset-models/${modelId}/custom-fields`,
      update: (modelId: string, fieldId: string) =>
        `/asset-models/${modelId}/custom-fields/${fieldId}`,
    },
    maintenanceTemplates: {
      delete: (modelId: string, templateId: string) =>
        `/asset-models/${modelId}/maintenance-templates/${templateId}`,
    },
    problemCategories: {
      assign: (modelId: string) => `/asset-models/${modelId}/problem-categories/assign`,
    },
  },
  ASSET_CATEGORIES: {
    list: () => "/asset-categories",
    detail: (id: string) => `/asset-categories/${id}`,
    create: () => "/asset-categories",
    update: (id: string) => `/asset-categories/${id}`,
    delete: (id: string) => `/asset-categories/${id}`,
  },
  MAINTENANCES: {
    list: () => "/maintenances",
    detail: (id: string) => `/maintenances/${id}`,
    create: () => "/maintenances",
    complete: (id: string) => `/maintenances/${id}/complete`,
    delete: (id: string) => `/maintenances/${id}`,
    modifyDetails: (id: string) => `/maintenances/${id}/modify-details`,
    linkFiles: (id: string) => `/maintenances/${id}/link-files`,
    parts: {
      list: (id: string) => `/maintenances/${id}/parts`,
      create: (id: string) => `/maintenances/${id}/parts`,
      update: (id: string, partId: string) => `/maintenances/${id}/parts/${partId}`,
      delete: (id: string, partId: string) => `/maintenances/${id}/parts/${partId}`,
    },
    tasks: {
      complete: (id: string, taskId: string) => `/maintenances/${id}/tasks/${taskId}/complete`,
      uncomplete: (id: string, taskId: string) => `/maintenances/${id}/tasks/${taskId}/uncomplete`,
    },
  },
  MAINTENANCE_TASK_TEMPLATES: {
    list: () => "/maintenance-task-templates",
    detail: (id: string) => `/maintenance-task-templates/${id}`,
    create: () => "/maintenance-task-templates",
    update: (id: string) => `/maintenance-task-templates/${id}`,
    delete: (id: string) => `/maintenance-task-templates/${id}`,
  },
  INSPECTIONS: {
    list: () => "/inspections",
    detail: (id: string) => `/inspections/${id}`,
    complete: (id: string) => `/inspections/${id}/complete`,
    tasks: {
      complete: (id: string, taskId: string) => `/inspections/${id}/tasks/${taskId}/complete`,
      uncomplete: (id: string, taskId: string) => `/inspections/${id}/tasks/${taskId}/uncomplete`,
    },
  },
  PARTS: {
    list: () => "/parts",
    detail: (id: string) => `/parts/${id}`,
    create: () => "/parts",
    update: (id: string) => `/parts/${id}`,
    delete: (id: string) => `/parts/${id}`,
    copy: () => "/parts/copy",
    forReplacement: () => "/parts/for-replacement",
    bookMaterial: (id: string) => `/parts/${id}/book-material`,
    discontinue: (id: string) => `/parts/${id}/mark-discontinued`,
    order: (id: string) => `/parts/${id}/mark-ordered`,
    stockHistory: (id: string) => `/parts/${id}/stock-history`,
    exportTemplate: () => "/parts/export-template",
    export: () => "/parts/export",
    import: () => "/parts/import",
    filterPresets: {
      list: () => "/part-filter-presets",
      create: () => "/part-filter-presets",
      update: (id: string) => `/part-filter-presets/${id}`,
      delete: (id: string) => `/part-filter-presets/${id}`,
      default: () => "/part-filter-presets/default",
    },
  },
  BRANCHES: {
    list: () => "/locations",
    detail: (id: string) => `/locations/${id}`,
    create: () => "/locations",
    update: (id: string) => `/locations/${id}`,
    delete: (id: string) => `/locations/${id}`,
    fromTemplate: () => "/locations/from-template",
    categories: {
      list: () => "/location-categories",
      detail: (id: string) => `/location-categories/${id}`,
      create: () => "/location-categories",
      update: (id: string) => `/location-categories/${id}`,
      delete: (id: string) => `/location-categories/${id}`,
      deleteInfo: (id: string) => `/location-categories/${id}/delete-info`,
    },
    geocoding: {
      geocode: () => "/geocoding/geocode",
      reverse: () => "/geocoding/reverse",
    },
  },
  WAREHOUSES: {
    list: () => "/warehouses",
    detail: (id: string) => `/warehouses/${id}`,
    create: () => "/warehouses",
    update: (id: string) => `/warehouses/${id}`,
    delete: (id: string) => `/warehouses/${id}`,
  },
  USER_WAREHOUSES: {
    list: () => "/user-warehouses",
    detail: (assignmentId: string) => `/user-warehouses/${assignmentId}`,
  },
  SUPPLIERS: {
    list: () => "/suppliers",
    detail: (id: string) => `/suppliers/${id}`,
    create: () => "/suppliers",
    update: (id: string) => `/suppliers/${id}`,
    delete: (id: string) => `/suppliers/${id}`,
  },
  TICKETS: {
    list: () => "/tickets",
    detail: (id: string) => `/tickets/${id}`,
    forward: (id: string) => `/tickets/${id}/forward`,
    queues: (id: string) => `/tickets/${id}/queues`,
    assignee: (id: string) => `/tickets/${id}/assignee`,
    priority: (id: string) => `/tickets/${id}/priority`,
  },
  TICKET_QUEUES: {
    list: () => "/queues",
    detail: (id: string) => `/queues/${id}`,
    create: () => "/queues",
    update: (id: string) => `/queues/${id}`,
    delete: (id: string) => `/queues/${id}`,
  },
  PROBLEM_CATEGORIES: {
    list: () => "/problem-categories",
    detail: (id: string) => `/problem-categories/${id}`,
    create: () => "/problem-categories",
    update: (id: string) => `/problem-categories/${id}`,
    delete: (id: string) => `/problem-categories/${id}`,
  },
  PERSONS: {
    list: () => "/persons",
    detail: (id: string) => `/persons/${id}`,
    create: () => "/persons",
    update: (id: string) => `/persons/${id}`,
    delete: (id: string) => `/persons/${id}`,
  },
  LABELS: {
    list: () => "/labels",
    detail: (id: string) => `/labels/${id}`,
    create: () => "/labels",
    update: (id: string) => `/labels/${id}`,
    delete: (id: string) => `/labels/${id}`,
    assignments: {
      replace: () => "/label-assignments/replace",
    },
  },
  USERS: {
    list: () => "/users",
    detail: (id: string) => `/users/${id}`,
    create: () => "/users",
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    magicLink: (id: string) => `/users/${id}/magic-link`,
    regenerateMagicLink: (id: string) => `/users/${id}/regenerate-magic-link`,
    resendInvitation: (id: string) => `/users/${id}/resend-invitation`,
    preferredLanguage: () => "/users/me/preferred-language",
    magicLinkAuthenticate: () => "/users/magic-link/authenticate",
  },
  API_KEYS: {
    list: () => "/api-keys",
    create: () => "/api-keys",
    updateExpiry: (id: string) => `/api-keys/${id}/expires`,
    revoke: (id: string) => `/api-keys/${id}/revoke`,
  },
  CONTACTS: {
    create: () => "/contacts",
    update: (id: string) => `/contacts/${id}`,
    delete: (id: string) => `/contacts/${id}`,
  },
  AUTH: {
    login: () => "/auth/login",
    me: () => "/auth/me",
    forgotPassword: () => "/auth/forgot-password",
    logout: () => "/auth/logout",
    resetPasswordValidate: () => "/auth/reset-password/validate",
    resetPassword: () => "/auth/reset-password",
  },
  EVENTS: {
    list: () => "/events",
    detail: (id: string) => `/events/${id}`,
    create: () => "/events",
    update: (id: string) => `/events/${id}`,
    delete: (id: string) => `/events/${id}`,
    complete: (id: string) => `/events/${id}/complete`,
  },
  EVENT_TYPES: {
    list: () => "/event-types",
    detail: (id: string) => `/event-types/${id}`,
    create: () => "/event-types",
    update: (id: string) => `/event-types/${id}`,
    delete: (id: string) => `/event-types/${id}`,
  },
  ACCOUNTS: {
    list: () => "/accounts",
    detail: (id: string) => `/accounts/${id}`,
    me: () => "/accounts/me",
    create: () => "/accounts",
    update: (id: string) => `/accounts/${id}`,
    delete: (id: string) => `/accounts/${id}`,
    deactivate: (id: string) => `/accounts/${id}/deactivate`,
    modules: () => "/modules",
    accountModules: () => "/account-modules",
    accountModulesBulk: () => "/account-modules/bulk",
    realtimeStream: () => "/events/stream",
    configPartPrint: () => "/account-config/part-print-config",
    configAssetPrint: () => "/account-config/asset-print-config",
    configLocationPrint: () => "/account-config/location-print-config",
    demoData: {
      load: (id: string) => `/accounts/${id}/demo-data/load`,
      status: (id: string) => `/accounts/${id}/demo-data/status`,
      delete: (id: string) => `/accounts/${id}/demo-data`,
    },
    licenseNotice: () => "/accounts/me/license-notice",
  },
  POLICIES: {
    list: () => "/auth/policies",
    detail: (id: string) => `/auth/policies/${id}`,
    create: () => "/auth/policies",
    update: (id: string) => `/auth/policies/${id}`,
    delete: (id: string) => `/auth/policies/${id}`,
    permissions: (id: string) => `/auth/policies/${id}/permissions`,
    users: (id: string) => `/auth/policies/${id}/users`,
    mine: () => "/auth/policies/me/policies",
  },
  SETTINGS: {
    get: () => "/settings",
    update: () => "/settings",
  },
  DASHBOARD: {
    layout: () => "/dashboard/layout",
    updateLayout: () => "/dashboard/layout",
  },
  ASSET_PARTS: {
    create: () => "/assetParts",
    update: (id: string) => `/assetParts/${id}`,
    delete: (id: string) => `/assetParts/${id}`,
  },
} as const;
